import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Users, Crown, ShoppingCart } from 'lucide-react';
import { Separator } from '../components/ui/separator';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function ClientBadge({ count }) {
  if (count >= 5) return <Badge className="bg-amber-500 text-white text-[10px]"><Crown className="w-3 h-3 mr-0.5 inline" />VIP</Badge>;
  if (count >= 3) return <Badge className="bg-primary text-primary-foreground text-[10px]">Fidèle</Badge>;
  if (count >= 2) return <Badge variant="outline" className="text-[10px]">Régulier</Badge>;
  return null;
}

export default function AdminClients() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  const token = localStorage.getItem('admin_token');

  const fetchClients = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(res.data);
    } catch (err) {
      if (err.response?.status === 401) navigate('/admin/login');
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!token) { navigate('/admin/login'); return; }
    fetchClients();
  }, [token, navigate, fetchClients]);

  const filtered = clients.filter(c => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      c.email?.toLowerCase().includes(q) ||
      c.prenom?.toLowerCase().includes(q) ||
      c.nom?.toLowerCase().includes(q)
    );
  });

  const totalClients = clients.length;
  const returningClients = clients.filter(c => c.total_purchases >= 2).length;
  const vipClients = clients.filter(c => c.total_purchases >= 5).length;
  const totalRevenue = clients.reduce((s, c) => s + (c.total_spent || 0), 0);

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-card border-b border-border px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Dashboard</span>
          </button>
          <Separator orientation="vertical" className="h-5" />
          <h1 className="text-base sm:text-xl font-bold text-foreground truncate" data-testid="admin-clients-title">
            Clients
          </h1>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6">
          <Card className="p-3 sm:p-4 text-center">
            <p className="text-xl sm:text-2xl font-bold text-primary">{totalClients}</p>
            <p className="text-xs text-muted-foreground">Clients</p>
          </Card>
          <Card className="p-3 sm:p-4 text-center">
            <p className="text-xl sm:text-2xl font-bold text-foreground">{returningClients}</p>
            <p className="text-xs text-muted-foreground">Fidèles (2+)</p>
          </Card>
          <Card className="p-3 sm:p-4 text-center">
            <p className="text-xl sm:text-2xl font-bold text-amber-600">{vipClients}</p>
            <p className="text-xs text-muted-foreground">VIP (5+)</p>
          </Card>
          <Card className="p-3 sm:p-4 text-center">
            <p className="text-lg sm:text-2xl font-bold text-accent">CHF {totalRevenue.toFixed(0)}</p>
            <p className="text-xs text-muted-foreground">CA total</p>
          </Card>
        </div>

        {/* Search */}
        <Input
          placeholder="Rechercher un client..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="mb-4 sm:max-w-xs"
          data-testid="client-search-input"
        />

        {/* Client list */}
        {loading ? (
          <p className="text-center py-8 text-muted-foreground">Chargement...</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Aucun client trouvé</p>
          </div>
        ) : (
          <div className="space-y-2">
            {/* Header row - desktop only */}
            <div className="hidden sm:grid sm:grid-cols-12 gap-3 px-4 py-2 text-xs font-medium text-muted-foreground uppercase">
              <div className="col-span-4">Client</div>
              <div className="col-span-3">Email</div>
              <div className="col-span-2 text-center">Commandes</div>
              <div className="col-span-2 text-right">Total dépensé</div>
              <div className="col-span-1 text-right">Statut</div>
            </div>

            {filtered.map((client, idx) => (
              <Card key={idx} className="p-3 sm:p-4" data-testid={`client-row-${idx}`}>
                {/* Mobile layout */}
                <div className="sm:hidden space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <p className="font-semibold text-sm text-foreground truncate">
                        {client.prenom} {client.nom}
                      </p>
                      <ClientBadge count={client.total_purchases} />
                    </div>
                    <span className="text-sm font-bold text-accent flex-shrink-0">
                      CHF {(client.total_spent || 0).toFixed(0)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span className="truncate">{client.email}</span>
                    <span className="flex items-center gap-1 flex-shrink-0">
                      <ShoppingCart className="w-3 h-3" />
                      {client.total_purchases} commande{client.total_purchases > 1 ? 's' : ''}
                    </span>
                  </div>
                </div>

                {/* Desktop layout */}
                <div className="hidden sm:grid sm:grid-cols-12 gap-3 items-center">
                  <div className="col-span-4 flex items-center gap-2 min-w-0">
                    <p className="font-semibold text-sm text-foreground truncate">
                      {client.prenom} {client.nom}
                    </p>
                  </div>
                  <div className="col-span-3 text-sm text-muted-foreground truncate">
                    {client.email}
                  </div>
                  <div className="col-span-2 text-center">
                    <span className="text-sm font-bold text-foreground">{client.total_purchases}</span>
                  </div>
                  <div className="col-span-2 text-right">
                    <span className="text-sm font-bold text-accent">CHF {(client.total_spent || 0).toFixed(2)}</span>
                  </div>
                  <div className="col-span-1 text-right">
                    <ClientBadge count={client.total_purchases} />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
